import { useState } from 'react'
import { Box, CircularProgress, keyframes } from '@mui/material'
import { L } from '../ui/LavDropdown'
import { MbIc } from './mbBits'
import RescheduleSlotPicker from './RescheduleSlotPicker'
import RescheduleConfirm from './RescheduleConfirm'

const slideIn = keyframes`from { transform: translateX(28px); opacity: 0.3 } to { transform: none; opacity: 1 }`
const slideUp = keyframes`from { transform: translateY(26px); opacity: 0.25 } to { transform: none; opacity: 1 }`

/**
 * RescheduleFlow — the Reschedule fast path from the Claude Design
 * "Appointment Details" spec: pick a slot → confirm (old → new, notify the
 * client) → done. Desktop slides the picker over the details panel
 * (design .apr-over); mobile takes over the screen (design .apr-take) and
 * confirms in a bottom sheet. Owns only the pending pick + the notify
 * toggle; the host (AppointmentDetailsDialog) performs the move.
 */
export default function RescheduleFlow({
	mobile = false,
	merchant,
	employees = [],
	loadingEmployees = false,
	bookedIds = [],
	serviceIds = [],
	excludeIds = [],
	mins,
	current,            // { date, time } merchant-local
	petNames,
	clientName,
	client,
	saving = false,
	onClose,
	onConfirm,          // ({ date, time, groomer, empIds, check, sent }) => void
	onOpenClient = null,
}) {
	const [pending, setPending] = useState(null)
	const [notify, setNotify] = useState(true)
	const sub = `${petNames} · ${mins} mins · services & price stay`

	const picker = loadingEmployees ? (
		<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
			<CircularProgress size={24} sx={{ color: L.primary }} />
		</Box>
	) : (
		<RescheduleSlotPicker
			merchant={merchant}
			employees={employees}
			bookedIds={bookedIds}
			serviceIds={serviceIds}
			excludeIds={excludeIds}
			mins={mins}
			current={current}
			value={pending}
			onPick={setPending}
		/>
	)

	const confirm = pending ? (
		<RescheduleConfirm
			mobile={mobile}
			from={current}
			fromEmpIds={bookedIds}
			to={pending}
			mins={mins}
			petNames={petNames}
			clientName={clientName}
			client={client}
			merchant={merchant}
			employees={employees}
			notify={notify}
			setNotify={setNotify}
			saving={saving}
			onBack={() => setPending(null)}
			onConfirm={(sent) => onConfirm({ ...pending, sent })}
			onOpenClient={onOpenClient}
		/>
	) : null

	return (
		<RescheduleOverlay mobile={mobile} title="Move appointment" sub={sub} onClose={onClose} footer={confirm}>
			{picker}
		</RescheduleOverlay>
	)
}

/**
 * RescheduleOverlay — the chrome the slot picker sits in (design
 * AprSlotOverlay): a desktop slide-over (.apr-over) or a mobile takeover
 * (.apr-take) with a back button, title and subtitle. Shared by the fast
 * path above and the When field of the edit flow, which opens the same
 * picker as "Change date & time". `footer` is layered over the body (the
 * confirm dialog / sheet).
 */
export function RescheduleOverlay({ mobile = false, title = 'Move appointment', sub, onClose, children, footer = null }) {
	if (mobile) {
		return (
			<Box
				role="dialog"
				aria-label={title}
				sx={{
					position: 'absolute', inset: 0, zIndex: 60, bgcolor: L.bg, color: L.text, fontFamily: L.font,
					display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: `${slideUp} 0.28s cubic-bezier(0.22, 0.9, 0.3, 1)`,
				}}
			>
				<Box sx={{ flex: 'none', p: 'calc(env(safe-area-inset-top, 0px) + 16px) 16px 10px' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '4px' }}>
						<Box
							component="button"
							type="button"
							aria-label="Back"
							onClick={onClose}
							sx={{
								width: 40, height: 40, flex: 'none', ml: '-10px', border: 'none', borderRadius: '11px',
								bgcolor: 'transparent', color: L.text, display: 'flex', alignItems: 'center', justifyContent: 'center',
								cursor: 'pointer', '&:active': { bgcolor: L.surface, color: L.primaryDeep },
							}}
						>
							<MbIc name="back" size={22} sw={2} />
						</Box>
						<Box component="h1" sx={{
							flex: 1, minWidth: 0, m: 0, fontSize: '23px', fontWeight: 800, letterSpacing: '-0.02em',
							whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
						}}>
							{title}
						</Box>
					</Box>
					<Box sx={{ mt: '3px', fontSize: '13px', fontWeight: 650, color: L.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{sub}
					</Box>
				</Box>
				<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '8px 16px 26px' }}>
					{children}
				</Box>
				{footer}
			</Box>
		)
	}

	return (
		<Box
			role="dialog"
			aria-label={title}
			sx={{
				position: 'absolute', inset: 0, zIndex: 36, bgcolor: L.bg, color: L.text, fontFamily: L.font,
				display: 'flex', flexDirection: 'column', animation: `${slideIn} 0.22s cubic-bezier(0.22, 0.9, 0.3, 1)`,
			}}
		>
			<Box sx={{ flex: 'none', display: 'flex', alignItems: 'center', gap: '10px', p: '20px 22px 12px' }}>
				<Box
					component="button"
					type="button"
					aria-label="Back"
					onClick={onClose}
					sx={{
						width: 28, height: 28, flex: 'none', borderRadius: '50%', border: `1px solid ${L.border}`, bgcolor: L.surface,
						color: L.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: 'inherit',
						'&:hover': { color: L.text, borderColor: L.muted },
					}}
				>
					<MbIc name="back" size={15} sw={2} />
				</Box>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Box component="h2" sx={{ m: 0, fontSize: '19px', fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</Box>
					<Box component="p" sx={{ m: '4px 0 0', fontSize: '12.5px', color: L.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</Box>
				</Box>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '4px 22px 24px', scrollbarWidth: 'thin', scrollbarColor: `${L.border} transparent` }}>
				{children}
			</Box>
			{footer}
		</Box>
	)
}
